import type { Game } from '../core/game';
import type { Affix, AffixId, MasterDie } from '../core/types';
import { dieMaterialOf } from './abyssApi';
import { callGame, hasMethod } from './gameApi';

/**
 * Adaptateur « Forge » — v0.46.0 (A94, lot B).
 *
 * Les recettes, leurs prix, leurs candidats, la vérification et l'application
 * vivent dans le cœur (`Game.forge*`, lot A). Cet adaptateur les appelle de
 * façon gardée, comme `ui/gameApi.ts` : sans le cœur, aucune recette, aucun
 * prix, un verdict « indisponible » — l'onglet n'existe de toute façon pas
 * tant que `hasForge()` est faux.
 *
 * Les formes (`ForgeRequest`, `ForgeOutcome`…) sont redéclarées
 * structurellement ici (aucun import de `core/forge`, qui n'existe pas encore
 * dans cette branche) ; elles suivent mot pour mot `CONTRAT_A94_COEUR.md` §6.
 */

export type ForgeRecipeId = 'reroll' | 'temper' | 'transmute' | 'fuse' | 'sublimate' | 'absolve';

/** Les six recettes, dans l'ordre d'affichage de la Forge. */
export const FORGE_RECIPE_IDS: readonly ForgeRecipeId[] = ['reroll', 'temper', 'transmute', 'fuse', 'sublimate', 'absolve'];

/** Une recette telle que `forgeRecipes()` la livre. */
export interface ForgeRecipeView {
  id: ForgeRecipeId;
  name: string;
  description: string;
  /** « 3 dés de la rareté du dé », « — »… */
  consumesText: string;
  /** Le nœud qui l'ouvre est-il acquis ? */
  unlocked: boolean;
  /** Nom du nœud à acquérir (« Carnet de recettes ») quand elle est verrouillée. */
  requiresNodeName: string;
}

export type ForgeRequest =
  | { recipe: 'reroll'; dieId: string; affixIndex: number }
  | { recipe: 'temper'; dieId: string }
  | { recipe: 'transmute'; dieId: string; consumedIds: string[] }
  | { recipe: 'fuse'; dieId: string; affixIndex: number; otherId: string; otherAffixIndex: number }
  | { recipe: 'sublimate'; dieId: string; consumedIds: string[] }
  | { recipe: 'absolve'; dieId: string; affixId: AffixId; consumedIds: string[] };

/** Verdict de `forgeCheck`. */
export interface ForgeVerdict {
  ok: boolean;
  /** Refus en français, tel que rendu par le cœur. */
  reason?: string;
  cost: number;
}

/** Résultat de `forgeApply`. */
export interface ForgeOutcomeView {
  recipe: ForgeRecipeId;
  die: MasterDie;
  cost: number;
  consumed: string[];
  /** Relance : les deux affixes proposés (le choix se fait dans la modale). */
  options?: [Affix, Affix];
  /** Sublimation : le dé est-il devenu Enrichi ? */
  success?: boolean;
}

/** Relance en attente de choix (`forgePending()`). */
export interface ForgePendingView {
  dieId: string;
  affixIndex: number;
  options: [Affix, Affix];
}

type Rec = Record<string, unknown>;

function rec(v: unknown): Rec {
  return typeof v === 'object' && v !== null ? (v as Rec) : {};
}

const isRecipeId = (v: unknown): v is ForgeRecipeId => FORGE_RECIPE_IDS.includes(v as ForgeRecipeId);

/** Recettes de la Forge (`forgeRecipes()`), vide sans le cœur. */
export function forgeRecipesOf(game: Game): ForgeRecipeView[] {
  const list = callGame(game, 'forgeRecipes');
  if (!Array.isArray(list)) return [];
  const out: ForgeRecipeView[] = [];
  for (const raw of list) {
    const o = rec(raw);
    if (!isRecipeId(o.id)) continue;
    out.push({
      id: o.id,
      name: typeof o.name === 'string' ? o.name : o.id,
      description: typeof o.description === 'string' ? o.description : '',
      consumesText: typeof o.consumesText === 'string' ? o.consumesText : '',
      unlocked: o.unlocked === true,
      requiresNodeName: typeof o.requiresNodeName === 'string' ? o.requiresNodeName : '',
    });
  }
  return out;
}

/**
 * Prix en or d'une recette (`forgePrice`, remise du Marteau comprise), ou
 * `null` quand le cœur répond « impossible » (infini) ou ne répond pas.
 */
export function forgePriceOf(game: Game, recipe: ForgeRecipeId, dieId?: string): number | null {
  const v = dieId === undefined ? callGame(game, 'forgePrice', recipe) : callGame(game, 'forgePrice', recipe, dieId);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Dés consommables proposés par le cœur, du moins bon au meilleur (`forgeCandidates`). */
export function forgeCandidatesOf(game: Game, recipe: ForgeRecipeId, dieId: string, affixId?: AffixId): MasterDie[] {
  const list =
    affixId === undefined
      ? callGame(game, 'forgeCandidates', recipe, dieId)
      : callGame(game, 'forgeCandidates', recipe, dieId, affixId);
  // A98 : le cœur ne propose que des dés Corrupted ; le filtre est redit ici par prudence.
  return Array.isArray(list) ? forgeableOnly(game, (list as MasterDie[]).filter((d) => typeof d?.id === 'string')) : [];
}

/** Verdict de `forgeCheck` ; sans le cœur, un refus « indisponible ». */
export function forgeCheckOf(game: Game, req: ForgeRequest): ForgeVerdict {
  const v = rec(callGame(game, 'forgeCheck', req));
  if (typeof v.ok !== 'boolean') return { ok: false, reason: 'La Forge n’est pas disponible.', cost: 0 };
  return {
    ok: v.ok,
    ...(typeof v.reason === 'string' ? { reason: v.reason } : {}),
    cost: typeof v.cost === 'number' && Number.isFinite(v.cost) ? v.cost : 0,
  };
}

/** Applique la recette (`forgeApply`) ; `null` si le cœur refuse ou est absent. */
export function forgeApplyOf(game: Game, req: ForgeRequest): ForgeOutcomeView | null {
  const o = rec(callGame(game, 'forgeApply', req));
  if (!isRecipeId(o.recipe) || typeof rec(o.die).id !== 'string') return null;
  const options = Array.isArray(o.options) && o.options.length === 2 ? (o.options as [Affix, Affix]) : undefined;
  return {
    recipe: o.recipe,
    die: o.die as MasterDie,
    cost: typeof o.cost === 'number' ? o.cost : 0,
    consumed: Array.isArray(o.consumed) ? (o.consumed as string[]) : [],
    ...(options ? { options } : {}),
    ...(typeof o.success === 'boolean' ? { success: o.success } : {}),
  };
}

/** Résout la Relance en attente (`forgeRerollChoose`) ; renvoie le dé résultat. */
export function forgeRerollChooseOf(game: Game, choice: 0 | 1 | 'keep'): MasterDie | null {
  const die = callGame(game, 'forgeRerollChoose', choice);
  return typeof rec(die).id === 'string' ? (die as MasterDie) : null;
}

/** Relance en attente (`forgePending()`), ou `null`. */
export function forgePendingOf(game: Game): ForgePendingView | null {
  const p = rec(callGame(game, 'forgePending'));
  if (typeof p.dieId !== 'string' || !Array.isArray(p.options) || p.options.length !== 2) return null;
  const idx = typeof p.affixIndex === 'number' ? p.affixIndex : 0;
  return { dieId: p.dieId, affixIndex: idx, options: p.options as [Affix, Affix] };
}

/** Le cœur expose-t-il toute la chaîne Forge ? (sert aux garde-fous de l'interface) */
export function hasForgeApi(game: Game): boolean {
  return hasMethod(game, 'forgeApply') && hasMethod(game, 'forgeCheck') && hasMethod(game, 'forgeRecipes');
}

// ─────────────────────────────────────── Ce que demande chaque recette

/** Raison de refus quand un dé de campagne se présente à la Forge (A98), repli du texte du cœur. */
export const NOT_FORGEABLE_TEXT = 'Seuls les dés des Corrupted GachaDice se forgent.';

/**
 * Ce dé peut-il entrer dans la Forge (A98) ? `game.isForgeable(die)` du cœur
 * (`{ ok, reason? }` : seuls les dés des Corrupted GachaDice se forgent, comme
 * cible et comme sacrifice). **Repli neutre** : sans cette méthode (cœur plus
 * ancien, où tout dé se forge), le dé est accepté — l'écran reste celui d'avant.
 */
export function isForgeableOf(game: Game, die: MasterDie): { ok: boolean; reason: string } {
  if (!hasMethod(game, 'isForgeable')) return { ok: true, reason: '' };
  const v = rec(callGame(game, 'isForgeable', die));
  if (typeof v.ok !== 'boolean') return { ok: true, reason: '' };
  return { ok: v.ok, reason: v.ok ? '' : typeof v.reason === 'string' && v.reason ? v.reason : NOT_FORGEABLE_TEXT };
}

/** Ne garde que les dés forgeables (cibles, seconds dés, sacrifices). */
export function forgeableOnly(game: Game, dice: readonly MasterDie[]): MasterDie[] {
  return dice.filter((d) => isForgeableOf(game, d).ok);
}

/** Nom exact du dé de départ, que la Forge ne touche jamais. */
export const GRANDFATHER_DIE_NAME = "Dé de l'aïeul";

/** Le Dé de l'aïeul (apostrophe droite ou typographique) est exclu des cibles. */
export function isGrandfatherDie(die: MasterDie): boolean {
  return typeof die.name === 'string' && die.name.replace(/\u2019/g, "'") === GRANDFATHER_DIE_NAME;
}

/** Ce qu'une recette demande à l'écran, étape par étape. */
export interface RecipeNeeds {
  /** Choisir un affixe du dé cible (Relance, Fusion). */
  affix: boolean;
  /** Choisir un second dé et son affixe (Fusion). */
  other: boolean;
  /** Choisir l'affixe à ajouter (Absolution). */
  absolve: boolean;
  /** Dés sacrifiés, pré-cochés (0 : aucun). */
  consume: number;
}

/**
 * Dés consommés par recette (`CONTRAT_A94_COEUR.md` §3) : 3, —, 10, les deux
 * dés de la Fusion (choisis comme second dé), 5, 10. Repli quand
 * `data/forge.ts` n'est pas là : le cœur reste juge (`forgeCheck`), ce tableau
 * ne sert qu'à **pré-cocher** le bon nombre.
 */
export const CONSUME_COUNT: Readonly<Record<ForgeRecipeId, number>> = {
  reroll: 0,
  temper: 0,
  transmute: 10,
  fuse: 0,
  sublimate: 5,
  absolve: 10,
};

/**
 * Import **dynamique** de `data/forge.ts` du cœur (aucune liaison statique :
 * absent, l'objet est vide ; présent, il porte `FORGE_CONSUMED_COUNT`).
 */
const FORGE_DATA = import.meta.glob('../data/forge.ts', { eager: true }) as Record<string, unknown>;

/** Nombre de dés sacrifiés par une recette : `FORGE_CONSUMED_COUNT` du cœur, sinon `CONSUME_COUNT`. */
export function consumeCountOf(recipe: ForgeRecipeId): number {
  const table = rec(rec(Object.values(FORGE_DATA)[0]).FORGE_CONSUMED_COUNT);
  const n = table[recipe];
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : CONSUME_COUNT[recipe];
}

/**
 * Dés à pré-cocher parmi les candidats (du moins bon au meilleur) : les `count`
 * premiers. Le **Trucage** veut des dés d'un même Matériau : on prend la
 * première famille de Matériau (dans l'ordre du moins bon) qui en compte assez,
 * à défaut la plus nombreuse — le verdict du cœur dit ce qui manque.
 */
export function preselectConsumed(recipe: ForgeRecipeId, candidates: readonly MasterDie[], count: number): string[] {
  if (count <= 0) return [];
  if (recipe !== 'transmute') return candidates.slice(0, count).map((d) => d.id);
  const groups = new Map<string, MasterDie[]>();
  for (const d of candidates) {
    const key = dieMaterialOf(d) ?? '';
    const list = groups.get(key);
    if (list) list.push(d);
    else groups.set(key, [d]);
  }
  let best: MasterDie[] = [];
  for (const list of groups.values()) {
    if (list.length >= count) return list.slice(0, count).map((d) => d.id);
    if (list.length > best.length) best = list;
  }
  return best.map((d) => d.id);
}

export function recipeNeeds(recipe: ForgeRecipeId): RecipeNeeds {
  return {
    affix: recipe === 'reroll' || recipe === 'fuse',
    other: recipe === 'fuse',
    absolve: recipe === 'absolve',
    consume: consumeCountOf(recipe),
  };
}

/** Description d'une demande, selon la recette et les choix faits (`null` : incomplet). */
export interface ForgeChoices {
  dieId: string | null;
  affixIndex: number | null;
  otherId: string | null;
  otherAffixIndex: number | null;
  affixId: AffixId | null;
  consumedIds: readonly string[];
}

/** Construit la `ForgeRequest` d'une recette, ou `null` tant qu'un choix manque. */
export function buildRequest(recipe: ForgeRecipeId, c: ForgeChoices): ForgeRequest | null {
  if (!c.dieId) return null;
  const consumedIds = [...c.consumedIds];
  switch (recipe) {
    case 'temper':
      return { recipe, dieId: c.dieId };
    case 'reroll':
      // A102 : la Relance ne sacrifie plus aucun dé.
      return c.affixIndex === null ? null : { recipe, dieId: c.dieId, affixIndex: c.affixIndex };
    case 'transmute':
    case 'sublimate':
      return { recipe, dieId: c.dieId, consumedIds };
    case 'fuse':
      return c.affixIndex === null || !c.otherId || c.otherAffixIndex === null
        ? null
        : { recipe, dieId: c.dieId, affixIndex: c.affixIndex, otherId: c.otherId, otherAffixIndex: c.otherAffixIndex };
    case 'absolve':
      return c.affixId === null ? null : { recipe, dieId: c.dieId, affixId: c.affixId, consumedIds };
  }
}
